import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUsers, createUser, updateUser, deleteUser } from '../redux/slices/usersSlice';
import { Modal, ConfirmDialog, LoadingSpinner, EmptyState } from '../components/Common';
import { useSearch } from '../context/SearchContext';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { printTable } from '../utils/printTable';

const emptyForm = { full_name: '', loginName: '', userType: 'user', password: '' };

const userTypeColors = {
  admin: 'bg-purple-100 text-purple-800',
  user: 'bg-blue-100 text-blue-800',
};

const Users = () => {
  const dispatch = useDispatch();
  const { searchTerm } = useSearch();
  const { items: users, loading, error } = useSelector((state) => state.users);
  const { user: currentUser } = useAuth();
  const { t } = useLanguage();

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [formData, setFormData] = useState(emptyForm);
  const [formError, setFormError] = useState('');

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  const filtered = users.filter((u) => {
    const term = searchTerm.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(term) ||
      u.loginName?.toLowerCase().includes(term)
    );
  });

  const openCreate = () => {
    setEditingId(null);
    setFormData(emptyForm);
    setFormError('');
    setShowModal(true);
  };

  const openEdit = (user) => {
    setEditingId(user.id);
    setFormData({
      full_name: user.full_name || '',
      loginName: user.loginName || '',
      userType: user.userType || 'user',
      password: '',
    });
    setFormError('');
    setShowModal(true);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError('');
    if (!editingId && !formData.password) {
      setFormError(t('users.passwordRequiredError'));
      return;
    }
    const payload = { ...formData };
    if (editingId && !payload.password) delete payload.password;

    const action = editingId ? updateUser({ id: editingId, data: payload }) : createUser(payload);
    dispatch(action)
      .unwrap()
      .then(() => setShowModal(false))
      .catch((err) => setFormError(err?.error || err?.message || t('users.saveFailed')));
  };

  const handleDelete = () => {
    dispatch(deleteUser(deleteTarget.id))
      .unwrap()
      .then(() => setDeleteTarget(null))
      .catch((err) => console.error('Failed to delete user:', err));
  };

  if (error) {
    return (
      <div className='card bg-red-50 border border-red-200'>
        <p className='text-red-600'>{t('users.errorLoading', { error })}</p>
      </div>
    );
  }

  const handlePrint = () => {
    printTable({
      title: t('users.title'),
      columns: [
        { header: t('common.name') },
        { header: t('users.loginName') },
        { header: t('users.role') },
      ],
      rows: filtered.map((u) => [u.full_name, u.loginName, u.userType]),
    });
  };

  return (
    <div>
      <div className='flex justify-between items-center mb-6'>
        <div>
          <h1 className='text-3xl font-bold text-gray-900'>{t('users.title')}</h1>
          <p className='text-gray-600 mt-1'>{t('users.subtitle')}</p>
        </div>
        <div className='flex items-center gap-3'>
          <button onClick={handlePrint} className='btn-secondary'>
            🖨️ {t('common.print')}
          </button>
          <button onClick={openCreate} className='btn-primary'>
            {t('users.addUser')}
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : filtered.length === 0 ? (
        <EmptyState
          icon='👤'
          title={t('users.noUsersTitle')}
          message={t('users.noUsersMessage')}
          actionLabel={t('users.addUser')}
          onAction={openCreate}
          hasItems={users.length > 0}
        />
      ) : (
        <div className='card overflow-x-auto'>
          <table className='min-w-full divide-y divide-gray-200'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('common.name')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('users.loginName')}</th>
                <th className='px-4 py-2 text-left text-xs font-medium text-gray-500'>{t('users.role')}</th>
                <th className='px-4 py-2 text-right text-xs font-medium text-gray-500'>{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200'>
              {filtered.map((u) => (
                <tr key={u.id} className='hover:bg-gray-50'>
                  <td className='px-4 py-3 text-sm font-medium text-gray-900'>
                    {u.full_name}
                    {currentUser?.id === u.id && (
                      <span className='ml-2 text-xs text-gray-400'>{t('users.you')}</span>
                    )}
                  </td>
                  <td className='px-4 py-3 text-sm text-gray-900'>{u.loginName || '-'}</td>
                  <td className='px-4 py-3'>
                    <span
                      className={`inline-block px-2 py-1 text-xs font-medium rounded-full ${userTypeColors[u.userType] || 'bg-gray-100 text-gray-800'}`}
                    >
                      {u.userType}
                    </span>
                  </td>
                  <td className='px-4 py-3 text-right space-x-3'>
                    <button onClick={() => openEdit(u)} className='text-sm text-blue-600 hover:text-blue-800'>
                      {t('common.edit')}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      disabled={currentUser?.id === u.id}
                      className='text-sm text-red-600 hover:text-red-800 disabled:text-gray-300 disabled:cursor-not-allowed'
                      title={currentUser?.id === u.id ? t('users.cantDeleteSelf') : undefined}
                    >
                      {t('common.delete')}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingId ? t('users.editUser') : t('users.createUser')}
        size='md'
      >
        <form onSubmit={handleSubmit}>
          <div className='space-y-4'>
            {formError && (
              <div className='bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg px-3 py-2'>
                {formError}
              </div>
            )}
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>{t('users.fullName')} *</label>
              <input
                type='text'
                value={formData.full_name}
                onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                className='input-field'
                required
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('users.loginName')} *</label>
                <input
                  type='text'
                  value={formData.loginName}
                  onChange={(e) => setFormData({ ...formData, loginName: e.target.value })}
                  className='input-field'
                  required
                />
              </div>
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>{t('users.role')}</label>
                <select
                  value={formData.userType}
                  onChange={(e) => setFormData({ ...formData, userType: e.target.value })}
                  className='input-field'
                >
                  <option value='user'>{t('nav.user')}</option>
                  <option value='admin'>{t('nav.admin')}</option>
                </select>
              </div>
            </div>
            <div>
              <label className='block text-sm font-medium text-gray-700 mb-2'>
                {t('users.password')} {editingId ? '' : '*'}
              </label>
              <input
                type='text'
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className='input-field'
                placeholder={editingId ? t('users.passwordPlaceholderEdit') : ''}
                required={!editingId}
              />
            </div>
            <div className='flex justify-end space-x-3 pt-4'>
              <button type='button' onClick={() => setShowModal(false)} className='btn-secondary'>
                {t('common.cancel')}
              </button>
              <button type='submit' className='btn-primary'>
                {editingId ? t('users.saveChanges') : t('users.createUser')}
              </button>
            </div>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title={t('users.deleteUser')}
        message={t('users.confirmDeletePermanent', { name: deleteTarget?.full_name })}
      />
    </div>
  );
};

export default Users;
